import { Injectable, OnDestroy } from '@angular/core';
import { Subject, Subscription } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { AuthService } from 'app/core/auth/auth.service';

/**
 * Keeps the access token "always alive" while a tab is open:
 *   1. Schedules a setTimeout to refresh ~60 s before the token expires.
 *   2. Re-schedules on every successful refresh (the token is rotated, so exp moves).
 *   3. On visibilitychange (tab regains focus), if the token is past the early-refresh
 *      window, refreshes immediately so the next user-triggered XHR doesn't see a 401.
 *
 * Started exactly once at app boot via {@link start}; tears down on app destroy.
 */
@Injectable({ providedIn: 'root' })
export class TokenRefreshService implements OnDestroy {

    /** Refresh this many seconds before the token's exp. */
    private static readonly EARLY_REFRESH_SECONDS = 60;

    /** Floor on the scheduled timer so we never spam-refresh on a degenerate / past-due token. */
    private static readonly MIN_TIMER_MS = 5_000;

    private _timer: any = null;
    private _started = false;
    private _destroy$ = new Subject<void>();
    private _tokenSub?: Subscription;

    constructor(private _authService: AuthService) {}

    /** Idempotent. Safe to call from AppComponent on every navigation. */
    start(): void {
        if (this._started) { return; }
        this._started = true;

        // Initial schedule from whatever's already in localStorage.
        this._scheduleFromCurrentToken();

        // Reschedule every time the access token changes (login / OTP / refresh / sign-out).
        this._tokenSub = this._authService.accessTokenChanged$
            .pipe(takeUntil(this._destroy$))
            .subscribe(() => this._scheduleFromCurrentToken());

        // Catch up when a backgrounded tab comes forward.
        if (typeof document !== 'undefined') {
            document.addEventListener('visibilitychange', this._onVisibilityChange);
        }
    }

    ngOnDestroy(): void {
        this._destroy$.next();
        this._destroy$.complete();
        this._tokenSub?.unsubscribe();
        this._clearTimer();
        if (typeof document !== 'undefined') {
            document.removeEventListener('visibilitychange', this._onVisibilityChange);
        }
    }

    // ------------------------------------------------------------------
    // Internals
    // ------------------------------------------------------------------

    private _onVisibilityChange = (): void => {
        if (document.visibilityState !== 'visible') { return; }
        // Re-arm the timer in case it fired while the tab was suspended.
        this._scheduleFromCurrentToken(/* immediateIfDueSoon */ true);
    };

    private _scheduleFromCurrentToken(immediateIfDueSoon = false): void {
        this._clearTimer();
        if (!this._authService.accessToken) { return; }

        const remaining = this._authService.secondsUntilExpiry();
        if (remaining < 0) { return; } // token already expired — let the interceptor handle the next 401

        const refreshIn = remaining - TokenRefreshService.EARLY_REFRESH_SECONDS;

        if (refreshIn <= 0) {
            if (immediateIfDueSoon) {
                this._refreshNow();
            } else {
                // Schedule a tiny delay so we don't recurse on token-change events.
                this._timer = setTimeout(() => this._refreshNow(), TokenRefreshService.MIN_TIMER_MS);
            }
            return;
        }

        const delay = Math.max(TokenRefreshService.MIN_TIMER_MS, refreshIn * 1000);
        this._timer = setTimeout(() => this._refreshNow(), delay);
    }

    private _refreshNow(): void {
        this._authService.refreshAccessToken().subscribe(/* the auth service handles failure */);
    }

    private _clearTimer(): void {
        if (this._timer != null) {
            clearTimeout(this._timer);
            this._timer = null;
        }
    }
}
